import {useMutation, useQuery} from "@tanstack/react-query";
import {useAuthToken} from "src/hooks/AuthHooks";
import {useAuth} from "src/auth/context";
import {usePortalContext} from "./PortalContext";
import {cacheKeyForClient} from "../services/shared/serviceConstants";
import {ICompany} from "../types/Company";

export type QueryContext = {
  client: ICompany
}

// Combine the local 'enabled' calculation with the
// any 'enabled' value passed into this hook, and
// combine them. Later, below, we combine the other
// TanStack options with the combined 'enabled' value.
// Quite a song and dance resulting from "Rules of Hooks"
const calculatedEnabled = ({
  isLoading,
  token,
  tanstackOptions,
}: {
  isLoading: boolean,
  token?: string,
  tanstackOptions: any,
}) => {
  const localEnabled = !isLoading && !!token;
  const {enabled: enabledArg, ...otherTanstackOptions} = tanstackOptions;
  const enabled = localEnabled && (
    // this was just enabledArg before 11 May 23
    (enabledArg === undefined) ? true : enabledArg
  );
  const result = {otherTanstackOptions, enabled};
  return result;
}

// Fetches the Access Token then enables the React Query.
// Used little - see src/services/SaasService/SaasView.ts
export const useQueryWithAccessToken = ({
  queryKey,
  queryFunction,
  queryFunctionVariables = {},
  tanstackOptions = {},
  userRole,
}: {
  queryKey: unknown[],
  queryFunction: Function,
  queryFunctionVariables: any,
  tanstackOptions?: any,
  userRole?: string,
}): any => {
  const {
    token,
    isLoading,
    error,
  } = useAuthToken();
  const {getAccessToken} = useAuth();

  if (error) console.error({error})

  const {clientInfo} = usePortalContext();
  if (!clientInfo) {
    // If there is no client set, we can't go on
    console.error('clientInfo not found');
    throw new Error('clientInfo not found');
  }

  let queryKeyWithClient = queryKey.map((qk: any) => {return cacheKeyForClient(qk, clientInfo.id)});

  // calculate enabled but also allow for a provided enabled value
  const {otherTanstackOptions, enabled} = calculatedEnabled({
    isLoading, token, tanstackOptions
  });

  return useQuery({
    queryKey: queryKeyWithClient,
    // signal supplied by tanstack to abort
    // tanstack.com/query/v5/docs/react/guides/query-cancellation
    queryFn: async ({signal}: any) => queryFunction({
      // Resolved per attempt rather than per render: a long-lived query page
      // would otherwise present the token it first saw, after it had expired.
      accessToken: await getAccessToken(),
      queryContext: {client: clientInfo},
      parameters: queryFunctionVariables,
      signal, // aborts on component garbage collection, I think
      userRole
    }),
    ...otherTanstackOptions, enabled
  });
};

// based on older version of TanStack.  Used by most queries.
export const useQueryWithAccessTokenWithoutContext = ({
                                          queryKey,
                                          queryFunction,
                                          queryFunctionVariables = {},
                                          tanstackOptions = {},
                                        }: {
  queryKey: unknown[],
  queryFunction: Function,
  queryFunctionVariables: any,
  tanstackOptions?: any,
}): any => {
  const {
    token,
    isLoading,
    error,
  } = useAuthToken();
  const {getAccessToken} = useAuth();

  if (error) console.error({error})

  // calculate enabled but also allow for a provided enabled value
  const {otherTanstackOptions, enabled} = calculatedEnabled({
    isLoading, token, tanstackOptions
  });

  return useQuery({
    queryKey: queryKey,
    // signal supplied by tanstack to abort
    // tanstack.com/query/v5/docs/react/guides/query-cancellation
    queryFn: async ({signal}: any) => queryFunction({
      accessToken: await getAccessToken(),
      parameters: queryFunctionVariables,
      signal // aborts on component garbage collection, I think
    }),
    ...otherTanstackOptions, enabled
  });
};


// this version has better 'enabled' handling than
// the legacy one below
export const useMutationWithAccessToken = ({
  // the actual mutation function, from services
  mutation,
  // variables defined when creating the mutations
  mutationCreationArgs = {},
  // built-in Tanstack options
  // we combine them with the enabled option, below
  // feature not currently used for mutations
  tanstackOptions = {},
}: {
  // the actual mutation function, from services
  mutation: any,
  // args when creating the mutations
  mutationCreationArgs?: any,
  // built-in Tanstack options
  // we combine them with the enabled option, below
  // feature not currently used for mutations
  tanstackOptions?: any,
}) => {
  const {
    token,
    isLoading,
    error,
  } = useAuthToken();
  const {getAccessToken} = useAuth();
  if (error) console.error({error})

  const {clientInfo} = usePortalContext();
  if (!clientInfo) {
    // If there is no client set, we can't go on
    console.error('clientInfo not found');
    throw new Error('clientInfo not found');
  }

  const {otherTanstackOptions, enabled} = calculatedEnabled({
    isLoading, token, tanstackOptions
  });

  const result = useMutation({
    mutationFn: async (
      // args when calling the mutation
      mutationCallArgs: any = {}
    ) => {

      return mutation(
        // create a single object combining
        // - the token,
        // - the mutation creation args
        // - the mutation call args.
        // this object goes to the mutation in services
        // TanStack limits the number of arguments to one.
        // This means we have to mix up the access token,
        // the creation args and the call args
        {
          // the token gets a unique name so it doesn't
          // clash with mutation-arg names
          hasuraAccessToken: await getAccessToken(),
          client_id: clientInfo.id,
          ...mutationCallArgs,
          ...mutationCreationArgs
        },
      )
    },
    ...otherTanstackOptions, enabled, // the TanStack options
  })

  return result;
};

// Executes a graphQL mutation without a token
// Use for requests to Hasura from unauthenticated users
export const usePublicMutation = ({
   mutation,
   mutationCreationArgs = {},
   tanstackOptions = {},
 }: {
  mutation: any,
  mutationCreationArgs?: any,
  tanstackOptions?: any,
}) => {
  const result = useMutation({
    mutationFn: async (
      // args when calling the mutation
      mutationCallArgs: any = {}
    ) => {
      return mutation(
        {
          ...mutationCallArgs,
          ...mutationCreationArgs
        },
      )
    },
    ...tanstackOptions,
  })

  return result;
};